import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { IPublicClientApplication } from '@azure/msal-browser';
import { catchError, from, Observable, switchMap } from 'rxjs';
import { BdAppConfigResponse } from '@bindoc-projects/app-config-domain-lib';

/** Everything {@link bdOpportunisticAuthInterceptor} needs, derived from config. */
export interface BdOpportunisticAuthConfig {
  /**
   * Origins the app already authenticates against somewhere, i.e. every origin
   * owning at least one `msalConfig.protectedResources` entry.
   */
  origins: ReadonlySet<string>;
  /** Scopes for the silent token request (the B2C client id). */
  scopes: string[];
  /**
   * Mirrors `BdAppConfigResponseHelper.msalInterceptorConfig`: when the
   * deployment configures a lightweight redirect page, silent renewals must
   * use it here too, or this path alone would reload the whole SPA in the
   * hidden iframe on every renewal.
   */
  silentRedirectUri?: string;
}

export const BD_OPPORTUNISTIC_AUTH_CONFIG =
  new InjectionToken<BdOpportunisticAuthConfig>('BD_OPPORTUNISTIC_AUTH_CONFIG');

/**
 * Builds the interceptor's config from the frontend config — never
 * hand-maintained.
 *
 * An origin counts as auth-capable as soon as ONE of its paths requires a
 * token. That is the signal that the backend understands our B2C tokens at
 * all, which is what makes it safe to offer one for its other paths too.
 * Backends with no protected resource whatsoever (code-api, mapbox, …) stay
 * out of the set and are never touched.
 */
export function createOpportunisticAuthConfig(
  appConfig: BdAppConfigResponse,
): BdOpportunisticAuthConfig {
  const origins = new Set<string>();
  appConfig.msalConfig.protectedResources.forEach((protectedResource) => {
    if (!protectedResource?.uri) {
      return;
    }
    try {
      origins.add(new URL(protectedResource.uri).origin);
    } catch {
      // A relative or malformed uri has no origin to derive — the MSAL
      // interceptor still handles it via the protectedResourceMap.
      console.warn(
        'Skipping protectedResources entry with unparsable uri',
        protectedResource.uri,
      );
    }
  });

  return {
    origins,
    scopes: appConfig.msalConfig.guardConfig.authRequest.scopes,
    silentRedirectUri: appConfig.msalConfig.silentRedirectUri,
  };
}

function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    // Relative URL — an app-local request, never one of the API origins.
    return null;
  }
}

/**
 * Attaches the signed-in user's bearer token to requests that MSAL's
 * `protectedResourceMap` does not cover, but only when a session already
 * exists. Never triggers interactive authentication.
 *
 * ## Why this exists (BDA-5977)
 *
 * clinics-api is permit-all: it answers an unauthenticated request with
 * HTTP 200 and a **silently reduced** payload rather than a 401. Concretely,
 * `GET /clinics/available-operating-numbers` returns 101 descriptors with a
 * token and 77 without — the 24 missing ones are the `ANNUAL_ACCOUNT`
 * (Finanzkennzahlen) metrics, which then vanish from the Klinik-Cube's metric
 * picker with no error, no 401 and no signal of any kind.
 *
 * The cause was that `protectedResources` enumerates individual clinics-api
 * sub-paths (`/clinics/operating-numbers`, `/clinics/by-filter`,
 * `/clinics/exports`, …) on top of a deliberately public `/clinics` base
 * (`AYS_CLINIC_RESOURCE_URL` is an `unprotectedApiEndpointKey`). MSAL v5
 * matches those entries strictly, so a path nobody remembered to add — here
 * `available-operating-numbers`, which merely *looks* like the listed
 * `operating-numbers` — silently goes out anonymous. It is not one oversight:
 * `by-condition`, `available-conditions` and `catalog-keys` are missing from
 * the list too, and `ClinicsFilterApi` keeps composing new paths from the
 * same base.
 *
 * ## Why the enumeration cannot simply be widened
 *
 * The obvious repair — protect the `/clinics` base, or the clinics-api host —
 * is unsafe. `protectedResourceMap` is binary: when an entry matches and no
 * account is signed in, MsalInterceptor falls back to *interactive* auth,
 * i.e. a B2C redirect. But clinics-api serves anonymous traffic on the same
 * host. Verified against staging with no session, all HTTP 200:
 *
 *   /clinics/home, /clinics/search  → GET /clinics/search
 *   /clinics/{clinicSn}             → GET /clinics/{sn}/metadata
 *                                     GET /clinics/{sn}/available-qb-years
 *                                     GET /clinics/{sn}/overview
 *
 * None of those four paths is in `protectedResources`, and none may be: the
 * public clinic pages are server-rendered for search engines, and the filter
 * keeps a deliberately public preview endpoint (see
 * `ClinicsFilterApi.publicResultPreview`). Widening the map would bounce
 * every anonymous visitor to the login screen — a worse bug than the one
 * being fixed.
 *
 * So the protected set can be neither widened (anonymous visitors break) nor
 * reliably enumerated (someone forgets a path, as happened four times). What
 * makes this tractable is that the distinction the list is trying to encode —
 * "does this caller get the richer answer?" — is not a property of the path
 * at all. It is a property of the *session*. This interceptor asks that
 * question directly instead, which is why it needs no list and cannot fall
 * out of date.
 *
 * ## Behaviour
 *
 * - anonymous user → request passes through untouched (unchanged behaviour,
 *   public pages keep working, no redirect);
 * - signed-in user → bearer token attached, so the backend answers with the
 *   full, permission-aware payload;
 * - never escalates to interaction: if the silent token request fails the
 *   request still goes out, and the failure is logged rather than swallowed.
 *
 * ## Relation to MsalInterceptor
 *
 * Both are registered; they do not conflict in either order. A request MSAL
 * already handled arrives here carrying an `Authorization` header and is
 * skipped. In the reverse order the token this interceptor attaches is for
 * the same scopes, and MsalInterceptor's `headers.set` simply overwrites it.
 * Truly protected paths therefore keep MsalInterceptor's interactive fallback
 * as their single source of truth.
 */
export function bdOpportunisticAuthInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const config = inject(BD_OPPORTUNISTIC_AUTH_CONFIG, { optional: true });
  const msalInstance = inject<IPublicClientApplication | null>(MSAL_INSTANCE, {
    optional: true,
  });

  // Not wired (server-side render, tests without MSAL) — nothing to do.
  if (!config || !msalInstance) {
    return next(request);
  }

  // Already authorized (see "Relation to MsalInterceptor" above).
  if (request.headers.has('Authorization')) {
    return next(request);
  }

  const origin = originOf(request.url);
  if (origin === null || !config.origins.has(origin)) {
    return next(request);
  }

  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  // Anonymous visitor: keep the request anonymous. Attaching nothing is the
  // whole point — this is the path that must never redirect.
  if (!account) {
    return next(request);
  }

  return from(
    msalInstance.acquireTokenSilent({
      scopes: config.scopes,
      account,
      ...(config.silentRedirectUri
        ? { redirectUri: config.silentRedirectUri }
        : {}),
    }),
  ).pipe(
    switchMap((result) =>
      next(
        request.clone({
          headers: request.headers.set(
            'Authorization',
            `Bearer ${result.accessToken}`,
          ),
        }),
      ),
    ),
    catchError(() => {
      // Silent renewal failed (expired B2C refresh token, third-party cookie
      // blocking, …). Deliberately NOT escalating to a redirect: that would
      // interrupt the user mid-page for a request that works without a token.
      // The request degrades to anonymous — the same reduced payload this
      // ticket is about — so it is logged instead of failing silently.
      console.warn(
        `[bd-auth] Silent token acquisition failed for ${request.url}; ` +
          'sending the request anonymously. The response may be missing ' +
          'permission-gated data.',
      );
      return next(request);
    }),
  );
}
